// crewbit-admin/src/pages/Money/Revenue.tsx
//
// SPEC: §Money.RevenueDashboard (filed 2026-05-04 during Phase B Session 140
// as gap 2 of 3 before §FinancialsPage.FinalRetire). Path A — dedicated
// /money/revenue route per spec §2.D1.
//
// Top-level admin page for /money/revenue. Composes PageHeader and
// thin-wraps the existing <RevenueTab> via the <useRevenue> envelope.
//
// Refresh policy is OWNED by useRevenue (period state internal to hook;
// 3 parallel direct-table reads via supabase-js on payment_ledger / expenses /
// invoices). NO realtime subscription; refetch fires when businessId or period
// changes. This page MUST NOT add visibility/window listeners of its own.
//
// <RequiresActiveBusinessGate> is applied at the route boundary in App.tsx;
// the !businessId branch here is defense-in-depth for transitions (mirrors
// Outstanding.tsx + Overview.tsx pattern).

import { useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton } from '../../components/common/Skeleton';
import { RevenueTab } from '../../components/money/RevenueTab';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import { useAuthStore } from '../../stores/authStore';
import { useRevenue } from '../../hooks/useRevenue';
import { admin, palette, spacing } from '../../styles/tokens';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

export function MoneyRevenuePage() {
  const business = useAuthStore((s) => s.business);
  const businessId = business?.id;
  const rev = useRevenue(businessId);

  useEffect(() => {
    document.title = 'CrewBit · Revenue';
  }, []);

  // Branch 1 — defensive no-business gate. Outer <RequiresActiveBusinessGate>
  // short-circuits at the route boundary; this is a fallback for transitions.
  if (!businessId) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Revenue" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <EmptyState
          icon={<Building2 size={22} />}
          title="Pick a business"
          subtitle="Switch to an active business from the sidebar to see revenue."
        />
      </div>
    );
  }

  // Branch 2 — initial load. useRevenue exposes only `loading`; period change
  // also flips loading true while the new range fetches.
  if (rev.loading) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Revenue" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.lg }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '16px 18px',
                background: palette.white,
                border: `1px solid ${admin.border}`,
                borderRadius: admin.cardRadius,
              }}
            >
              <Skeleton width={80} height={10} />
              <div style={{ height: 12 }} />
              <Skeleton width={120} height={26} />
            </div>
          ))}
        </div>
        <Skeleton width="100%" height={280} />
      </div>
    );
  }

  // Branch 3 — happy path. Thin-wrap RevenueTab with useRevenue envelope.
  // RevenueTab owns the period selector + 4 metric cards + 2 charts +
  // monthly summary table; this page just plumbs hook into component.
  return (
    <div style={pageStyle}>
      <PageHeader title="Revenue" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
      <RevenueTab
        revenue={rev.revenue}
        expenses={rev.expenses}
        netProfit={rev.netProfit}
        hstCollected={rev.hstCollected}
        hstOnExpenses={rev.hstOnExpenses}
        hstOwing={rev.hstOwing}
        revenueByMonth={rev.revenueByMonth}
        expensesByCategory={rev.expensesByCategory}
        period={rev.period}
        setPeriod={rev.setPeriod}
      />
    </div>
  );
}
